import { useApp } from '@/App';
import GenericErrorMessage from '@/components/GenericErrorMessage';
import { DocumentItem } from '@/components/modals/DocumentModal';
import { useModals } from '@/components/modals/ModalProvider';
import DataRow from '@/libs/antd/components/DataRow';
import FleetModal from '@/libs/antd/components/Modal';
import FleetTitle from '@/libs/antd/components/Title';
import ViewManagementShareholder from '@/libs/antd/components/ViewManagementShareholder';
import { FormAlert, useForm } from '@/libs/antd/form';
import ANTD_DEFAULT_PROPS from '@/libs/antd/props';
import formRule from '@/libs/antd/rules';
import fetcher from '@/libs/fleet-api/fetcher';
import {
	cancelSlikRobotik,
	getProspectCustomerChecking,
	// getSlikRobotikResult,
	requestSLIKCCA,
	RequestSLIKCCAPayload,
	submitCCANextSLIK,
	submitCCAReturnSLIK,
} from '@/libs/fleet-api/services/prospect';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
	Alert,
	Button,
	Col,
	Divider,
	Flex,
	Form,
	Input,
	Progress,
	Radio,
	Result,
	Row,
	Space,
	Table,
} from 'antd';
import dayjs from 'dayjs';
import { FC, Fragment, useState } from 'react';
import { InfoCircleFilled, ReloadOutlined } from '@ant-design/icons';
import rule from '@/libs/antd/rules';

// type RequestSLIKCCAForm = {
// 	payload: Omit<RequestSLIKCCAPayload, 'prospectId'>;
// 	form: {
// 		notes: string;
// 	};
// };

export type CCACustomerCheckingFormProps = {
	prospectId: string;
};

const CCACustomerCheckingForm: FC<CCACustomerCheckingFormProps> = ({
	prospectId = '943P2024060009',
}) => {
	const { modal, openPreviewModal } = useModals();
	const { navigate, notification } = useApp();
	const {
		openCustomerDetail,
		openCustomerGroupDetail,
		openDocumentModal,
		openProspectDetail,
	} = useModals();
	const { data, isLoading, isFetching, isError, refetch } = useQuery({
		queryKey: ['getProspectCustomerChecking', prospectId] as const,
		queryFn: ({ queryKey }) => {
			const [, id] = queryKey;
			return getProspectCustomerChecking(id);
		},
	});

	const [successModal, setSuccessModal] = useState(false);
	const [hasPopupOpened, setHasPopupOpened] = useState(true);

	const {
		mainInformation,
		customerCompanyManagementInformation,
		customerCompanyShareholderInformation,
		customerBusinessDocuments,
		customerLegalDocuments,
		customerReferralDocuments,
		approvalHistory,
		slikCheck,
		slikInquiryMetric,
	} = data?.data || {};

	// TODO: get result from slik robotik info and show popup
	const {
		isRobotic = false,
		isRoboticFailed = false,
		roboticStatus,
	} = slikInquiryMetric || {};

	const slikOnProgress =
		(roboticStatus?.done ?? 0) + (roboticStatus?.notFound ?? 0);
	const slikTotal = roboticStatus?.total ?? 0;

	const isJF = mainInformation?.customerType?.id === 1;
	const isNext =
		slikCheck?.slikCheckingStatus?.id === 1 &&
		!!slikCheck.slikCheckingDate &&
		dayjs().diff(slikCheck.slikCheckingDate, 'day') <= 90;

	const isNoRequiredFiles =
		!slikCheck?.slikFormFile.id || !slikCheck?.applicationLetterFile.id;

	const { form, value, fieldName } = useForm<RequestSLIKCCAPayload>();

	const requestSLIKCCAMutation = useMutation({ mutationFn: requestSLIKCCA });
	const submitCCAReturnSLIKMutation = useMutation({
		mutationFn: submitCCAReturnSLIK,
	});

	const submitCCANextSLIKMutation = useMutation({
		mutationFn: submitCCANextSLIK,
	});

	const cancelSlikRobotikMutation = useMutation({
		mutationFn: cancelSlikRobotik,
	});

	const handleCancelSlikRobotik = async () => {
		try {
			await cancelSlikRobotikMutation.mutateAsync(prospectId);
		} catch {
			notification.error({
				message: 'Error',
				description: 'Failed to cancel SLIK robotik',
			});
		}
	};

	const [showReturnSLIKModal, setShowReturnSLIKModal] = useState(false);
	const [returnTextArea, setReturnTextArea] = useState('');

	if (isError) return <GenericErrorMessage />;

	return (
		<Fragment>
			<FleetModal
				open={isRobotic && hasPopupOpened}
				keyboard={false}
				cancelText="Close"
				// if robotic is failed, show ok button
				okButtonProps={!isRoboticFailed ? { hidden: true } : {}}
				onOk={
					isRoboticFailed
						? () => navigate('CCAAnalisaSLIKPage', { prospectId })
						: undefined
				}
				onCancel={() => setHasPopupOpened(false)}
				closable
				footer={
					!isRoboticFailed ? (
						<Flex
							dir="row"
							justify="center"
							style={{ width: '100%' }}
							gap={16}
						>
							<Button
								onClick={async () => {
									const isConfirmed = await modal.confirm({
										title: 'Batalkan SLIK robotik?',
										subTitle:
											'Apakah anda yakin ingin membatalkan proses SLIK robotik? Anda perlu analisa dan mengupload IDEB manual untuk melanjutkan proses.',
									});
									if (!isConfirmed) return;
									handleCancelSlikRobotik();
								}}
							>
								Cancel SLIK Robotik
							</Button>
							<Button
								type="primary"
								onClick={() => setHasPopupOpened(false)}
							>
								Tutup
							</Button>
						</Flex>
					) : (
						<Flex
							dir="row"
							justify="center"
						>
							<Button
								type="primary"
								onClick={async () => {
									await handleCancelSlikRobotik();
									navigate('CCAAnalisaSLIKPage');
								}}
							>
								Proses SLIK Manual
							</Button>
						</Flex>
					)
				}
			>
				{
					// if robotic is done, show success modal
					!isRoboticFailed ? (
						<Flex
							style={{
								display: 'flex',
								justifyContent: 'center',
								alignItems: 'center',
								minHeight: 200,
								flexDirection: 'column',
								gap: 24,
							}}
						>
							<InfoCircleFilled
								style={{ color: '#1557BC', fontSize: 72 }}
								size={72}
							/>

							<span
								style={{ fontWeight: 600, fontSize: 16, textAlign: 'center' }}
							>
								Proses Pengecekan SLIK sedang berlangsung, <br />
								Mohon tunggu beberapa saat...
							</span>

							<Flex
								justify="end"
								style={{ marginBottom: -16 }}
							>
								<span>{slikOnProgress}</span>/<span>{slikTotal}</span>
							</Flex>
							<Progress
								percent={Math.round((slikOnProgress / slikTotal) * 100)}
							/>
							<Button
								size="small"
								type="link"
								onClick={(e) => {
									e.preventDefault();
									refetch();
								}}
								loading={isFetching}
								icon={<ReloadOutlined />}
								style={{
									marginTop: -16,
									marginBottom: 0,
									padding: '4px 8px',
									height: 'auto',
									fontSize: 12,
								}}
							>
								Refresh
							</Button>
						</Flex>
					) : (
						<Flex
							style={{
								display: 'flex',
								justifyContent: 'center',
								alignItems: 'center',
								minHeight: 200,
								flexDirection: 'column',
								gap: 24,
							}}
						>
							<InfoCircleFilled
								style={{ color: '#1557BC', fontSize: 72 }}
								size={72}
							/>
							<span
								style={{ fontWeight: 600, fontSize: 16, textAlign: 'center' }}
							>
								Maaf, terjadi kesalahan pada proses pengecekan SLIK <br />{' '}
								dikarenakan gangguan teknis. Silakan lanjutkan proses <br />{' '}
								pengecekan SLIK secara manual.
							</span>
						</Flex>
					)
				}
			</FleetModal>
			<FleetTitle level={1}>Request SLIK</FleetTitle>
			<Divider />
			<FleetTitle>Main Information</FleetTitle>
			<DataRow
				loading={isLoading}
				style={{
					marginBottom: 20,
				}}
				items={[
					[
						[
							'Nama Customer',
							<a
								href="#"
								className="trigger-popup"
								onClick={(e) => {
									e.preventDefault();
									openCustomerDetail(mainInformation!.customerId);
								}}
							>
								{mainInformation?.customerName}
							</a>,
						],
						[
							'Prospect ID',
							<a
								href="#"
								className="trigger-popup"
								onClick={(e) => {
									e.preventDefault();
									openProspectDetail(mainInformation!.prospectId);
								}}
							>
								{mainInformation?.prospectId}
							</a>,
						],
					],
					[
						['Kelas Industri', mainInformation?.industrySector.industryClass],
						['Sumber Order', mainInformation?.orderSource],
					],
					[
						[
							'Portofolio Guideline',
							mainInformation?.industrySector.portofolioGuideline,
						],
						[
							'Group Customer BMPP',
							mainInformation?.customerGroup?.id ? (
								<a
									href="#"
									className="trigger-popup"
									onClick={(e) => {
										e.preventDefault();
										openCustomerGroupDetail(mainInformation!.customerGroup.id);
									}}
									aria-disabled={!mainInformation?.customerGroup.value}
								>
									{mainInformation?.customerGroup.value ?? '-'}
								</a>
							) : (
								'-'
							),
						],
					],
					[
						[
							'Dokumen',
							<a
								href="#"
								className="trigger-popup"
								onClick={(e) => {
									e.preventDefault();

									const documents: DocumentItem[] = [];
									const getDocumentType = (name: string) => {
										if (name.endsWith('pdf')) return 'pdf';
										if (name.endsWith('jpeg')) return 'jpeg';
										if (name.endsWith('jpg')) return 'jpg';
										if (name.endsWith('png')) return 'png';
									};

									if (
										customerBusinessDocuments &&
										customerBusinessDocuments.length
									) {
										customerBusinessDocuments
											.filter((doc) => doc.file)
											.forEach((document) => {
												documents.push({
													name: document.documentType,
													type: getDocumentType(document.file.name)!,
													url: document.file.url!,
												});
											});
									}

									if (customerLegalDocuments && customerLegalDocuments.length) {
										customerLegalDocuments
											.filter((doc) => doc.file)
											.forEach((document) => {
												documents.push({
													name: document.documentType,
													type: getDocumentType(document.file.name)!,
													url: document.file.url!,
												});
											});
									}

									if (
										customerReferralDocuments &&
										customerReferralDocuments.length
									) {
										customerReferralDocuments
											.filter((doc) => doc.file)
											.forEach((document) => {
												documents.push({
													name: document.documentType,
													type: getDocumentType(document.file.name)!,
													url: document.file.url!,
												});
											});
									}

									if (documents.length) {
										openDocumentModal(documents);
									} else {
										alert('no document found');
									}
								}}
							>
								View Dokumen
							</a>,
						],
					],
				]}
			/>
			<FleetTitle>View Data Checking SLIK</FleetTitle>
			{isJF && (
				<Fragment>
					<DataRow
						items={[
							[
								[
									'Status Pengecekan SLIK',
									slikCheck?.slikCheckingStatus?.value,
								],
								slikCheck?.slikCheckingStatus?.id === 1
									? [
											'Tanggal Pengecekan SLIK',
											slikCheck.slikCheckingDate &&
												dayjs(slikCheck.slikCheckingDate).format('DD/MM/YYYY'),
										]
									: undefined,
							],
						]}
					/>
					{slikCheck?.slikCheckingDate &&
						dayjs().diff(slikCheck.slikCheckingDate, 'day') > 90 && (
							<FormAlert
								alerts={[
									'Data SLIK debitur Anda sudah melewati masa berlaku, mohon untuk melakukan pembaruan data SLIK agar dapat melanjutkan proses.',
								]}
							/>
						)}
					<Divider />
				</Fragment>
			)}
			<DataRow
				items={[
					[
						[
							'View Form FAPP',
							slikCheck?.slikFormFile.id ? (
								<a
									href="#"
									onClick={(e) => {
										e.preventDefault();
										if (!slikCheck?.slikFormFile) return;
										openPreviewModal(slikCheck.slikFormFile);
									}}
								>
									Form FAPP
								</a>
							) : (
								'-'
							),
						],
						[
							'View Form PDP',
							slikCheck?.applicationLetterFile.id ? (
								<a
									href="#"
									onClick={(e) => {
										e.preventDefault();
										if (!slikCheck?.applicationLetterFile) return;
										openPreviewModal(slikCheck.applicationLetterFile);
									}}
								>
									Form-PDP
								</a>
							) : (
								'-'
							),
						],
					],
				]}
			/>
			<Form
				{...ANTD_DEFAULT_PROPS.FORM}
				form={form}
				onFinish={async (values) => {
					if (isNext) {
						const isConfirmed = await modal.confirm({
							title: 'Apakah data yang Anda inputkan sudah benar?',
						});

						if (!isConfirmed) return;
						await submitCCANextSLIKMutation.mutateAsync({
							prospectId: mainInformation!.prospectId,
							additionalInformation: values.additionalInformation,
							isRecheckSlik: values.isRecheckSlik,
						});
						navigate('CCARequestSLIKPage');
						return;
					}

					try {
						// setSuccessModal(false);
						const isConfirmed = await modal.confirm({
							title: 'Request SLIK untuk prospect ini?',
						});
						if (!isConfirmed) return;
						await requestSLIKCCAMutation.mutateAsync({
							...values,
							prospectId,
						});
						notification.success({
							message: 'Success',
							description: 'Request SLIK Berhasil',
						});
						navigate('CCARequestSLIKPage', { prospectId });
						// setSuccessModal(true);
					} catch (error) {
						if (fetcher.isError(error)) {
							const message = await error.json();
							notification.error({
								duration: 20,
								message: `Error ID: ${message?.metadata?.debugInfo?.errorId ?? 'Unknown'}`,
								description: (
									<div>
										{message?.metadata?.debugInfo?.messages &&
										message?.metadata?.debugInfo?.messages.length ? (
											<ul>
												{message?.metadata?.debugInfo?.messages.map(
													(msg: string) => <li>{msg}</li>,
												)}
											</ul>
										) : (
											<pre>Opps, Something went wrong</pre>
										)}
									</div>
								),
							});
						}
					}
				}}
			>
				{isJF && isNext && (
					<Form.Item
						label="Apakah perlu dilakukan pengecekan ulang terhadap data SLIK?"
						rules={[rule.required()]}
						labelCol={{ flex: 'none' }}
						name={'isRecheckSlik'}
						initialValue={data?.data?.isRecheckSlik}
					>
						<Radio.Group style={{ display: 'flex' }}>
							<Radio
								value={true}
								style={{ lineHeight: '1' }}
							>
								Ya, Perlu
							</Radio>
							<Radio
								value={false}
								style={{ lineHeight: '1' }}
							>
								Tidak Perlu
							</Radio>
						</Radio.Group>
					</Form.Item>
				)}
				<Divider />
				<FleetTitle>Management/Shareholder Listing</FleetTitle>
				<ViewManagementShareholder
					managements={customerCompanyManagementInformation ?? []}
					shareholders={customerCompanyShareholderInformation ?? []}
					style={{
						marginBottom: 20,
					}}
				/>
				<FleetTitle>Approval History</FleetTitle>
				<Table
					bordered
					loading={isLoading}
					pagination={false}
					dataSource={
						approvalHistory?.map((approval, key) => {
							return {
								key,
								...approval,
							};
						}) ?? []
					}
					columns={[
						{
							title: 'No',
							render: (_, record) => record.no,
						},
						{
							title: 'Name',
							render: (_, record) => record.approvalBy,
						},
						{
							title: 'Date',
							render: (_, record) => record.approvalDate,
						},
						{
							title: 'Process',
							render: (_, record) => record.approvalProcess,
						},
						{
							title: 'Status',
							align: 'center',
							render: (_, record) => {
								return (
									<span
										className={`review-status ${record.approvalStatus.toLowerCase()}`}
									>
										{record.approvalStatus}
									</span>
								);
							},
						},
						{
							title: 'Approval Notes',
							render: (_, record) => record.approvalNote,
						},
					]}
					style={{ marginBottom: 20 }}
				/>
				<Row gutter={[40, 20]}>
					<Col span={24}>
						<Form.Item
							name={fieldName(['additionalInformation'])}
							label="Catatan"
							labelCol={{
								span: 6,
								style: {
									alignSelf: 'center',
								},
							}}
							wrapperCol={{
								style: {
									marginLeft: -10,
								},
							}}
							rules={[formRule.required()]}
						>
							<Input.TextArea
								rows={4}
								disabled={isRobotic}
							/>
						</Form.Item>
					</Col>
				</Row>
				{value?.isRecheckSlik &&
					(!slikCheck?.slikFormFile.id ||
						!slikCheck?.applicationLetterFile.id) && (
						<Alert
							style={{ marginTop: 40 }}
							message={
								<div style={{ color: 'red' }}>
									<b>FRM</b> belum melengkapi <b>dokumen FAPP</b> dan{' '}
									<b>dokumen PDP</b>. Untuk dapat mengajukan pengecekan SLIK,
									kedua dokumen tersebut harus dilengkapi terlebih dahulu.
									Silakan ajukan{' '}
									<a
										href="#"
										onClick={(e) => {
											e.preventDefault();
											setShowReturnSLIKModal(true);
										}}
									>
										<b>RETURN</b>
									</a>{' '}
									agar FRM dapat melengkapinya.
								</div>
							}
							type="error"
							showIcon={false}
							className="custom-alert-info"
						/>
					)}
				<Space style={{ display: 'flex', justifyContent: 'flex-end' }}>
					{isNext && value?.isRecheckSlik === false ? (
						<Button
							type="primary"
							htmlType="submit"
							loading={submitCCANextSLIKMutation.isPending}
							disabled={!isNext}
							style={{
								width: 200,
								display: 'block',
								marginTop: 60,
							}}
						>
							NEXT
						</Button>
					) : (
						<Fragment>
							<Button
								type="default"
								style={{
									width: 200,
									display: 'block',
									marginTop: 60,
								}}
								loading={submitCCAReturnSLIKMutation.isPending}
								onClick={async () => {
									// 	const isConfirmed = await modal.confirm({
									// 		title: 'Return Prospect?',
									// 	});
									// 	if (!isConfirmed) return;
									// 	try {
									// 		await submitCCAReturnSLIKMutation.mutateAsync({
									// 			additionalInformation: value!.additionalInformation!,
									// 			prospectId: mainInformation!.prospectId!,
									// 			isRecheckSlik: value?.isRecheckSlik,
									// 		});
									// 		navigate('CCARequestSLIKPage');
									// 	} catch (error) {
									// 		if (fetcher.isError(error)) {
									// 			const message = await error.json();
									// 			notification.error({
									// 				duration: 20,
									// 				message: `Error ID: ${message?.metadata?.debugInfo?.errorId ?? 'Unknown'}`,
									// 				description: (
									// 					<div>
									// 						{message?.metadata?.debugInfo?.messages &&
									// 						message?.metadata?.debugInfo?.messages.length ? (
									// 							<ul>
									// 								{message?.metadata?.debugInfo?.messages.map(
									// 									(msg: string) => <li>{msg}</li>,
									// 								)}
									// 							</ul>
									// 						) : (
									// 							<pre>Opps, Something went wrong</pre>
									// 						)}
									// 					</div>
									// 				),
									// 			});
									// 		}
									// 	}
									// }}
									setShowReturnSLIKModal(true);
								}}
							>
								RETURN
							</Button>
							<Button
								type="primary"
								htmlType="submit"
								loading={requestSLIKCCAMutation.isPending}
								style={{
									width: 200,
									display: 'block',
									marginTop: 60,
								}}
								disabled={
									isRobotic ||
									(isNext && value?.isRecheckSlik === null) ||
									(isNoRequiredFiles && value?.isRecheckSlik === true)
								}
							>
								REQUEST
							</Button>
						</Fragment>
					)}
				</Space>
			</Form>
			<FleetModal
				open={successModal}
				keyboard={false}
				cancelText="BACK"
				okText="NEXT"
				onOk={() => {
					setSuccessModal(false);
					navigate('CCARequestSLIKPage', { prospectId });
				}}
				onCancel={() => {
					setSuccessModal(false);
					navigate('CCARequestSLIKPage', { prospectId });
				}}
			>
				<Result
					status="success"
					title={<div style={{ fontSize: 30 }}>Sukses</div>}
				/>
			</FleetModal>
			<FleetModal
				open={showReturnSLIKModal}
				keyboard={false}
				cancelText="BACK"
				okText="SUBMIT"
				okButtonProps={{
					disabled: !returnTextArea.length,
					loading: submitCCAReturnSLIKMutation.isPending,
				}}
				cancelButtonProps={{
					disabled: submitCCAReturnSLIKMutation.isPending,
				}}
				onOk={async () => {
					await submitCCAReturnSLIKMutation.mutateAsync({
						additionalInformation: returnTextArea,
						prospectId: mainInformation!.prospectId!,
						isRecheckSlik: value?.isRecheckSlik,
					});
					setShowReturnSLIKModal(false);
					navigate('CCARequestSLIKPage');
				}}
				onCancel={() => setShowReturnSLIKModal(false)}
			>
				<Result
					status="info"
					title="Return Notes"
				/>
				<Input.TextArea
					rows={4}
					value={returnTextArea}
					onChange={(e) => setReturnTextArea(e.target.value)}
				/>
			</FleetModal>
		</Fragment>
	);
};

export default CCACustomerCheckingForm;
